import TableLayout from '@components/@material-extend/TableLayout'
import TableHeadSection from '@components/@material-extend/TableLayout/TableHeadSection'
import {
  Avatar,
  Button,
  Checkbox,
  Pagination,
  Stack,
  TableCell,
  TableRow,
} from '@mui/material'
import { Create, Delete, Visibility, VisibilityOff } from '@mui/icons-material'
import { SHOP_STATUS, ShopStatusOptions } from '@constants/common.constants'
import { useTranslation } from 'react-i18next'
import useShops from '@containers/Shops/useShops'
import CreateForm from '@containers/Shops/CreateForm'
import { useConfirm } from '@components/Confirm'
import DialogBase from '@components/@material-extend/DialogBase'
import ContentBox from '@layouts/AdminLayout/elements/ContentBox'
import { useEffect, useState } from 'react'
import FilterForm, { FilterParams } from '@containers/Shops/ShopsFilter'
import { ShopItem, ShopCreateParams } from '@services/shops.services'
import { Link } from 'react-router-dom'
import _ from 'lodash'

const Shops = () => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<number[]>([])
  const [open, setOpen] = useState<boolean>(false)
  const {
    paging,
    data,
    meta,
    metaCreate,
    fetch,
    changeStatus,
    deleteShop,
    create,
  } = useShops()
  const confirm = useConfirm()
  const [filter, setFilter] = useState<any>({
    status: undefined,
  })
  const getSearchParams = () => {
    let searchParams = {}
    if (paging) {
      searchParams = {
        page: paging.current_page,
        per_page: paging.per_page,
        ...filter,
      }
    } else {
      searchParams = {
        page: 1,
        per_page: 20,
        ...filter,
      }
    }
    return searchParams
  }
  useEffect(() => {
    fetch(getSearchParams())
  }, [filter])

  const handleSelectAll = () => {
    const allSelected = selected.length === data?.length && data?.length > 0
    if (allSelected) {
      setSelected([])
    } else {
      setSelected(_.map(data, (d) => Number(d.id)))
    }
  }

  const handleChecked = (id: number) => {
    const exist = !!_.find(selected, (i) => i === id)
    if (exist) {
      setSelected(_.filter(selected, (s) => s !== id))
    } else {
      setSelected([...selected, id])
    }
  }

  const handleFilter = (data: FilterParams) => {
    setFilter(data)
  }

  const onChangeStatus = (status: SHOP_STATUS) => {
    confirm({
      title: t('SYSCOMMON.update_confirm_title'),
      description: t('shops.update_confirm_desc', {
        target: _.find(ShopStatusOptions, (s) => s.value === status)?.name,
      }),
    })
      .then(() => {
        changeStatus({
          ids: selected,
          status: status,
        })
      })
      .catch(() => null)
  }

  const deleteShops = () => {
    confirm({
      title: t('shops.delete_confirm_title'),
      description: t('shops.delete_confirm_desc', {
        target: _.find(ShopStatusOptions, (s) => s.value === status)?.name,
      }),
    })
      .then(() => {
        const searchParams = getSearchParams()
        const currentSelected = [...selected]
        deleteShop({
          ids: currentSelected,
          ...searchParams,
        })
        setSelected([])
      })
      .catch(() => null)
  }

  const paginationHandler = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    fetch({
      page: page,
      per_page: 20,
      ...filter,
    })
  }

  const handleCreate = (params: ShopCreateParams) => {
    create(params)
    setOpen(false)
  }

  const renderRow = (item: ShopItem, index: number) => {
    const data = item.attributes
    return (
      <TableRow key={index}>
        <TableCell>
          <Checkbox
            onClick={() => handleChecked(Number(item.id))}
            checked={!!_.find(selected, (id) => id === Number(item.id))}
          />
        </TableCell>
        <TableCell scope="row" sx={{ position: 'relative' }}>
          <Avatar alt={data.name} sx={{ borderRadius: '0' }} src={data.image} />
        </TableCell>
        <TableCell scope="row" sx={{ position: 'relative' }}>
          {data.name}
        </TableCell>
        <TableCell align="center">{data.manager}</TableCell>
        <TableCell align="center">{data.phone}</TableCell>
        <TableCell align="center">{data.fax}</TableCell>
        <TableCell align="center">
          {_.find(ShopStatusOptions, (s) => s.value === data.status)?.name}
        </TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined">
              <Link
                style={{ textDecoration: 'none', color: 'inherit' }}
                to={`/admin/shops/${item.id}/edit`}
              >
                {t('button.edit')}
              </Link>
            </Button>
          </Stack>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <ContentBox>
      <DialogBase
        onClose={() => setOpen(false)}
        open={open}
        title={t('SYSCOMMON.register')}
        content={
          <CreateForm
            meta={metaCreate}
            onCreate={handleCreate}
            close={() => setOpen(false)}
          />
        }
      />
      <TableLayout
        renderRow={renderRow}
        data={!_.isEmpty(data) && data ? data : []}
        isLoading={meta.pending}
        pagintionBox={{ sx: { pt: 2 } }}
        pagination={
          paging && (
            <Pagination
              page={paging?.current_page}
              count={paging?.total_pages}
              onChange={paginationHandler}
            />
          )
        }
        headSection={
          <TableHeadSection
            title={t('SYSCOMMON.shop_management')}
            total={paging?.total_count}
            actions={<FilterForm onFilter={handleFilter} />}
          />
        }
        filter={
          <Stack justifyContent="flex-start" direction={'row'} spacing={1}>
            <Button
              startIcon={<VisibilityOff />}
              color="primary"
              variant="outlined"
              disabled={_.isEmpty(selected) ? true : false}
              onClick={() => onChangeStatus(SHOP_STATUS.SHOP_STATUS_DRAFT)}
            >
              {t('SYSCOMMON.private')}
            </Button>
            <Button
              startIcon={<Visibility />}
              color="primary"
              variant="outlined"
              disabled={_.isEmpty(selected) ? true : false}
              onClick={() => onChangeStatus(SHOP_STATUS.SHOP_STATUS_PUBLISHED)}
            >
              {t('SYSCOMMON.publish')}
            </Button>
            <Button
              startIcon={<Delete />}
              disabled={_.isEmpty(selected) ? true : false}
              color="primary"
              variant="outlined"
              onClick={() => deleteShops()}
            >
              {t('SYSCOMMON.delete')}
            </Button>
            <Button
              startIcon={<Create />}
              color="secondary"
              variant="contained"
              onClick={() => setOpen(true)}
            >
              {t('SYSCOMMON.register')}
            </Button>
          </Stack>
        }
        head={
          <TableRow>
            <TableCell width={40} align="center">
              <Checkbox
                checked={selected.length === data?.length && data?.length > 0}
                onClick={handleSelectAll}
              />
            </TableCell>
            <TableCell sx={{ minWidth: 70, width: 60 }}></TableCell>
            <TableCell sx={{ minWidth: 70 }}>{t('shops.name')}</TableCell>
            <TableCell sx={{ minWidth: 70 }}>{t('shops.manager')}</TableCell>
            <TableCell sx={{ minWidth: 70 }}>{t('shops.phone')}</TableCell>
            <TableCell sx={{ minWidth: 70 }}>{t('shops.fax')}</TableCell>
            <TableCell sx={{ minWidth: 70 }} align="center">
              {t('SYSCOMMON.status')}
            </TableCell>
            <TableCell
              sx={{ minWidth: 70, width: '100px' }}
              align="center"
            ></TableCell>
          </TableRow>
        }
      />
    </ContentBox>
  )
}

export default Shops
